import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, X, Check, Shield } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const ROL_LABELS = { admin:'Administrador', supervisor:'Supervisor', monitorista:'Monitorista' };
const ROL_COLORS = { admin:'var(--danger)', supervisor:'var(--warning)', monitorista:'var(--accent)' };

export default function UsuariosPage() {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ nombre:'', apellido:'', email:'', password:'', rol:'monitorista', telefono:'', activo:true });

  const fetchUsuarios = async () => {
    setLoading(true);
    try { const res = await api.get('/auth/usuarios'); setUsuarios(res.data.data); }
    catch(e) { toast.error('Error al cargar usuarios'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchUsuarios(); }, []);

  const openCreate = () => { setForm({ nombre:'', apellido:'', email:'', password:'', rol:'monitorista', telefono:'', activo:true }); setEditItem(null); setShowModal(true); };
  const openEdit = (u) => { setForm({ nombre:u.nombre, apellido:u.apellido, email:u.email, password:'', rol:u.rol, telefono:u.telefono||'', activo:!!u.activo }); setEditItem(u); setShowModal(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form };
      if (!body.password) delete body.password;
      if (editItem) { await api.put(`/auth/usuarios/${editItem.id}`, body); toast.success('Usuario actualizado'); }
      else { await api.post('/auth/usuarios', body); toast.success('Usuario creado'); }
      setShowModal(false); fetchUsuarios();
    } catch(err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`¿Desactivar al usuario ${u.nombre} ${u.apellido}?`)) return;
    try { await api.delete(`/auth/usuarios/${u.id}`); toast.success('Usuario desactivado'); fetchUsuarios(); }
    catch(e) { toast.error('Error'); }
  };

  const f = (k) => (e) => setForm(p => ({...p, [k]: e.target.value}));

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Usuarios del Sistema</div>
          <div className="page-subtitle">{usuarios.length} usuarios registrados</div>
        </div>
        <button className="btn btn-primary" onClick={openCreate}><Plus size={14}/> Nuevo Usuario</button>
      </div>

      <div className="card">
        <div className="table-wrap">
          {loading ? <div className="loading-screen"><div className="spinner"/></div> : (
            <table>
              <thead>
                <tr><th>Nombre</th><th>Email</th><th>Rol</th><th>Teléfono</th><th>Último Acceso</th><th>Estado</th><th>Acciones</th></tr>
              </thead>
              <tbody>
                {usuarios.length===0 && <tr><td colSpan={7} className="table-empty">Sin usuarios</td></tr>}
                {usuarios.map(u => (
                  <tr key={u.id}>
                    <td>
                      <div style={{fontWeight:600, fontSize:13}}>{u.nombre} {u.apellido}</div>
                    </td>
                    <td style={{fontSize:12,color:'var(--text-muted)',fontFamily:'var(--font-mono)'}}>{u.email}</td>
                    <td>
                      <span className="badge" style={{background:`${ROL_COLORS[u.rol]}22`, color:ROL_COLORS[u.rol]}}>{ROL_LABELS[u.rol]}</span>
                    </td>
                    <td style={{fontSize:12}}>{u.telefono||'—'}</td>
                    <td style={{fontSize:11,color:'var(--text-muted)'}}>
                      {u.ultimo_acceso ? format(new Date(u.ultimo_acceso), "dd/MM/yy HH:mm", { locale:es }) : 'Nunca'}
                    </td>
                    <td>
                      <span style={{display:'flex',alignItems:'center',gap:4}}>
                        <span style={{width:6,height:6,borderRadius:'50%',background:u.activo?'var(--success)':'var(--danger)'}}/>
                        <span style={{fontSize:11,color:u.activo?'var(--success)':'var(--text-muted)'}}>{u.activo?'Activo':'Inactivo'}</span>
                      </span>
                    </td>
                    <td>
                      <div style={{display:'flex',gap:6}}>
                        <button className="btn-icon" onClick={()=>openEdit(u)}><Pencil size={13}/></button>
                        <button className="btn-icon" onClick={()=>handleDelete(u)} style={{color:'var(--danger)',borderColor:'var(--danger-dim)'}}><Trash2 size={13}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:500}}>
            <div className="modal-header">
              <span className="modal-title"><Shield size={16} style={{marginRight:8}}/>{editItem ? 'Editar' : 'Nuevo'} Usuario</span>
              <button className="btn-icon" onClick={()=>setShowModal(false)}><X size={16}/></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="grid-2">
                  <div className="form-group"><label className="form-label">Nombre *</label><input className="form-control" value={form.nombre} onChange={f('nombre')} required placeholder="Juan"/></div>
                  <div className="form-group"><label className="form-label">Apellido *</label><input className="form-control" value={form.apellido} onChange={f('apellido')} required placeholder="Pérez García"/></div>
                  <div className="form-group"><label className="form-label">Email *</label><input className="form-control" type="email" value={form.email} onChange={f('email')} required placeholder="usuario@empresa.com"/></div>
                  <div className="form-group"><label className="form-label">Teléfono</label><input className="form-control" value={form.telefono} onChange={f('telefono')} placeholder="55-1234-5678"/></div>
                  <div className="form-group"><label className="form-label">Rol *</label>
                    <select className="form-control" value={form.rol} onChange={f('rol')}>
                      <option value="monitorista">Monitorista</option>
                      <option value="supervisor">Supervisor</option>
                      <option value="admin">Administrador</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">{editItem ? 'Nueva Contraseña (dejar vacío para no cambiar)' : 'Contraseña *'}</label>
                    <input className="form-control" type="password" value={form.password} onChange={f('password')} required={!editItem} placeholder="Mínimo 8 caracteres"/>
                  </div>
                </div>
                <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer'}}>
                  <input type="checkbox" checked={!!form.activo} onChange={e=>setForm(p=>({...p,activo:e.target.checked}))} style={{width:16,height:16}}/>
                  <span style={{fontSize:13}}>Usuario activo</span>
                </label>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary"><Check size={14}/> Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
